import React, { useState, useEffect, useContext } from "react";

import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";
import { Loader2, Eye, EyeOff } from "lucide-react";

import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogFooter,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "../ui/select";

import { i18n } from "../../translate/i18n";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import QueueSelect from "../QueueSelect";
import { AuthContext } from "../../context/Auth/AuthContext";
import { Can } from "../Can";
import BarraDeSalvar from "../BarraDeSalvar";
import useWhatsApps from "../../hooks/useWhatsApps";

const UserSchema = Yup.object().shape({
	name: Yup.string()
		.min(2, "Muito curto")
		.max(50, "Muito longo")
		.required("Campo obrigatório"),
	password: Yup.string().min(5, "Muito curto").max(50, "Muito longo"),
	email: Yup.string().email("E-mail inválido").required("Campo obrigatório"),
});

const ESCOPO_EM_PALAVRAS = {
	own: "vê apenas os atendimentos que estiverem com ela",
	team: "vê os atendimentos da equipe dela",
	queues: "vê os atendimentos dos departamentos em que participa",
	company: "vê todos os atendimentos da empresa",
};

const resumoDoCargo = (cargo) => {
	if (!cargo) {
		return "Sem cargo, a pessoa entra sem permissão nenhuma. Escolha um.";
	}

	const escopo = ESCOPO_EM_PALAVRAS[cargo.ticketScope];
	const aviso =
		cargo.ticketScope === "own"
			? " Atenção: ela não recebe conversa nova do departamento sozinha."
			: "";

	return `Com este cargo, a pessoa ${escopo}.${aviso}`;
};

const CartaoDoPerfil = ({ titulo, ajuda, className, children }) => (
	<section className={`min-w-0 rounded-xl border bg-card ${className || ""}`}>
		<div className="border-b p-4">
			<h2 className="text-sm font-semibold">{titulo}</h2>
			<p className="mt-0.5 text-xs text-muted-foreground">{ajuda}</p>
		</div>
		<div className="p-4">{children}</div>
	</section>
);

// `pagina`: o mesmo formulário dentro da página do usuário (`/users/:id`),
// sem o Dialog. Salvar não fecha nada: grava, relê e avisa `onSalvo`.
// `somenteLeitura` mostra o cadastro sem deixar mexer. Criar usuário e o
// "meu perfil" do layout continuam no modal.
// `meuPerfil`: o "Meu perfil" do layout. Grava em `PUT /users/me`, que não
// exige `users:edit` — só nome, e-mail e senha; o resto é de quem administra.
const UserModal = ({
	open,
	onClose,
	userId,
	pagina = false,
	somenteLeitura = false,
	onSalvo,
	meuPerfil = false,
}) => {
	const initialState = {
		name: "",
		email: "",
		password: "",
		senhaAtual: "",
		profile: "user",
	};

	const { user: loggedInUser } = useContext(AuthContext);

	const [user, setUser] = useState(initialState);
	const [selectedQueueIds, setSelectedQueueIds] = useState([]);
	const [showPassword, setShowPassword] = useState(false);
	const [whatsappId, setWhatsappId] = useState("");
	const { loading, whatsApps } = useWhatsApps();

	// Cargo: define as permissões e o que a pessoa enxerga na lista de
	// atendimentos. Sem este campo, a tela de Cargos não servia para nada —
	// dava para criar cargos e ninguém podia ser posto neles.
	const [cargos, setCargos] = useState([]);
	const [roleId, setRoleId] = useState("");
	// Na página, salvar relê o usuário para o formulário voltar a "sem
	// alterações" com o que ficou gravado (senha em branco de novo).
	const [recarga, setRecarga] = useState(0);

	useEffect(() => {
		const fetchUser = async () => {
			if (!userId) return;
			try {
				const { data } = await api.get(`/users/${userId}`);
				setUser((prevState) => {
					return { ...prevState, ...data };
				});
				const userQueueIds = data.queues?.map((queue) => queue.id);
				setSelectedQueueIds(userQueueIds);
				setWhatsappId(data.whatsappId ? data.whatsappId : "");
				setRoleId(data.roleId ? String(data.roleId) : "");
			} catch (err) {
				toastError(err);
			}
		};

		fetchUser();
	}, [userId, open, recarga]);

	useEffect(() => {
		if (meuPerfil || (!open && !pagina)) return;

		(async () => {
			try {
				const { data } = await api.get("/roles");
				setCargos(data || []);
			} catch {
				// Sem a lista, o campo some — não é motivo para impedir de
				// salvar o resto do cadastro.
			}
		})();
	}, [open, pagina, meuPerfil]);

	// Departamentos, conexão e cargo moram fora do Formik: o `dirty` dele não
	// os enxerga, e a barra de salvar da página precisa enxergar.
	const idsDasFilas = (ids) => [...(ids || [])].sort().join(",");
	const cargoGravado = user.roleId ? String(user.roleId) : "";
	const selecoesAlteradas =
		idsDasFilas(selectedQueueIds) !==
			idsDasFilas(user.queues?.map((q) => q.id)) ||
		String(whatsappId || "") !== String(user.whatsappId || "") ||
		roleId !== cargoGravado;

	const descartarSelecoes = () => {
		setSelectedQueueIds(user.queues?.map((q) => q.id) || []);
		setWhatsappId(user.whatsappId || "");
		setRoleId(cargoGravado);
	};

	const handleClose = () => {
		onClose();
		setUser(initialState);
	};

	const handleSaveUser = async (values, acoes) => {
		if (meuPerfil) {
			try {
				await api.put("/users/me", {
					name: values.name,
					email: values.email,
					password: values.password,
					senhaAtual: values.senhaAtual,
				});
				toast.success("Perfil salvo.");
				handleClose();
			} catch (err) {
				// O modal fica aberto com o que foi digitado: errar a senha atual
				// não pode apagar a nova.
				toastError(err);
			}
			return;
		}

		const userData = { ...values, whatsappId, queueIds: selectedQueueIds };
		try {
			// O cargo tem rota própria (com registro de quem mudou), então vai
			// numa chamada separada — depois de existir o usuário.
			let alvo = userId;

			if (userId) {
				await api.put(`/users/${userId}`, userData);
			} else {
				const { data } = await api.post("/users", userData);
				alvo = data?.id;
			}

			// Na edição, só quando o cargo mudou: a rota exige
			// `users:roles`, e mandá-la sempre fazia quem só edita cadastro
			// levar "sem permissão" ao salvar um nome.
			if (alvo && (!userId || roleId !== cargoGravado)) {
				await api.put(`/users/${alvo}/role`, {
					roleId: roleId ? Number(roleId) : null,
				});
			}

			toast.success(i18n.t("userModal.success"));
			if (pagina) {
				// A releitura não traz a senha: o usuário relido é igual ao de
				// antes e o Formik não se refaz — a senha ficava no campo e a
				// barra seguia "Alterações não salvas".
				acoes?.resetForm({
					values: { ...values, password: "", senhaAtual: "" },
				});
				setRecarga((n) => n + 1);
				onSalvo?.();
				return;
			}
		} catch (err) {
			toastError(err);
			// Na página o que foi digitado fica, para corrigir e tentar de novo.
			if (pagina) return;
		}
		handleClose();
	};

	// Antes da resposta o formulário teria os campos vazios, editáveis.
	if (pagina && !user.id) {
		return (
			<p className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
				<Loader2 className="h-4 w-4 animate-spin" />
				Carregando o usuário…
			</p>
		);
	}

	const cabecalho = (
		<DialogHeader>
			<DialogTitle>
				{meuPerfil
					? "Meu perfil"
					: userId
					? i18n.t("userModal.title.edit")
					: i18n.t("userModal.title.add")}
			</DialogTitle>
		</DialogHeader>
	);

	const formulario = (
		<Formik
			initialValues={user}
			enableReinitialize={true}
			validationSchema={UserSchema}
			// Devolver a promessa mantém o "salvando" até a resposta.
			onSubmit={(values, acoes) => handleSaveUser(values, acoes)}
		>
			{({
				values,
				setFieldValue,
				touched,
				errors,
				isSubmitting,
				dirty,
				resetForm,
			}) => {
				// Blocos do formulário: a página põe cada um num cartão da grade, o
				// modal empilha. `@container`: as colunas seguem a largura do bloco.
				const dados = (
					<div className="@container">
					<div className="grid gap-4 @xs:grid-cols-2">
						<div className="space-y-1.5">
							<Label htmlFor="name">{i18n.t("userModal.form.name")}</Label>
							<Field as={Input} id="name" name="name" autoFocus={!pagina} />
							{touched.name && errors.name && (
								<p className="text-xs text-destructive">{errors.name}</p>
							)}
						</div>
						<div className="space-y-1.5">
							<Label htmlFor="email">{i18n.t("userModal.form.email")}</Label>
							<Field as={Input} id="email" name="email" />
							{touched.email && errors.email && (
								<p className="text-xs text-destructive">{errors.email}</p>
							)}
						</div>
						<div className="space-y-1.5">
							<Label htmlFor="password">{i18n.t("userModal.form.password")}</Label>
							<div className="relative">
								<Field
									as={Input}
									id="password"
									name="password"
									type={showPassword ? "text" : "password"}
									className="pr-9"
									placeholder={pagina ? "Em branco, não muda" : undefined}
								/>
								<button
									type="button"
									onClick={() => setShowPassword((e) => !e)}
									aria-label={showPassword ? "Esconder senha" : "Mostrar senha"}
									className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
								>
									{showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
								</button>
							</div>
							{touched.password && errors.password && (
								<p className="text-xs text-destructive">{errors.password}</p>
							)}
						</div>
						{/* Trocar senha ou e-mail (o login) pede a senha de agora. */}
						{meuPerfil &&
							(values.password ||
								(values.email || "").trim().toLowerCase() !==
									(user.email || "").toLowerCase()) && (
								<div className="space-y-1.5">
									<Label htmlFor="senhaAtual">Senha atual</Label>
									<Field
										as={Input}
										id="senhaAtual"
										name="senhaAtual"
										type="password"
										autoComplete="current-password"
										required
									/>
									<p className="text-xs text-muted-foreground">
										Para trocar a senha ou o e-mail de acesso.
									</p>
								</div>
							)}
						<Can
							role={loggedInUser.profile}
							perform="user-modal:editProfile"
							yes={() => !meuPerfil && (
								<div className="space-y-1.5">
									<Label>{i18n.t("userModal.form.profile")}</Label>
									<Select
										value={values.profile}
										onValueChange={(v) => setFieldValue("profile", v)}
									>
										<SelectTrigger>
											<SelectValue />
										</SelectTrigger>
										<SelectContent>
											<SelectItem value="admin">Admin</SelectItem>
											<SelectItem value="user">User</SelectItem>
										</SelectContent>
									</Select>
								</div>
							)}
						/>
					</div>
					</div>
				);

				const cargo = (
					<div className="space-y-1.5">
						<Label>Cargo</Label>
						<Select
							value={roleId || "none"}
							onValueChange={(v) => setRoleId(v === "none" ? "" : v)}
						>
							<SelectTrigger>
								<SelectValue placeholder="Sem cargo" />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="none">Sem cargo</SelectItem>
								{cargos.map((c) => (
									<SelectItem key={c.id} value={String(c.id)}>
										{c.name}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
						{/* O que o cargo escolhido significa, em uma frase: quem
						    cadastra usuário não deveria precisar abrir outra tela
						    para descobrir. */}
						<p className="text-xs leading-relaxed text-muted-foreground">
							{resumoDoCargo(cargos.find((c) => String(c.id) === String(roleId)))}
						</p>
					</div>
				);

				const atuacao = (
					<div className="space-y-4">
						<QueueSelect
							selectedQueueIds={selectedQueueIds}
							onChange={(ids) => setSelectedQueueIds(ids)}
						/>
						{!loading && (
							<div className="space-y-1.5">
								<Label>{i18n.t("userModal.form.whatsapp")}</Label>
								<Select
									value={whatsappId ? String(whatsappId) : "none"}
									onValueChange={(v) => setWhatsappId(v === "none" ? "" : v)}
								>
									<SelectTrigger>
										<SelectValue />
									</SelectTrigger>
									<SelectContent>
										<SelectItem value="none">&nbsp;</SelectItem>
										{whatsApps.map((whatsapp) => (
											<SelectItem key={whatsapp.id} value={String(whatsapp.id)}>
												{whatsapp.name}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
							</div>
						)}
					</div>
				);

				const podeCargo = (bloco) => (
					<Can
						role={loggedInUser.profile}
						perform="user-modal:editProfile"
						yes={() => cargos.length > 0 && bloco}
					/>
				);
				const podeFilas = (bloco) => (
					<Can
						role={loggedInUser.profile}
						perform="user-modal:editQueues"
						yes={() => bloco}
					/>
				);

				if (pagina) {
					return (
						<Form className="space-y-4">
							<fieldset
								disabled={somenteLeitura}
								className="grid min-w-0 items-start gap-4 lg:grid-cols-2 2xl:grid-cols-3"
							>
								<CartaoDoPerfil
									className="lg:col-span-2 2xl:col-span-1"
									titulo="Dados de acesso"
									ajuda="Nome que aparece para os clientes e o e-mail de login."
								>
									{dados}
								</CartaoDoPerfil>
								{podeFilas(
									<CartaoDoPerfil
										titulo="Departamentos e conexão"
										ajuda="De quais filas recebe conversa e por qual número responde por padrão."
									>
										{atuacao}
									</CartaoDoPerfil>
								)}
								{podeCargo(
									<CartaoDoPerfil
										titulo="Cargo"
										ajuda="Define as permissões e quais atendimentos a pessoa enxerga."
									>
										{cargo}
									</CartaoDoPerfil>
								)}
							</fieldset>
							{!somenteLeitura && (
								<BarraDeSalvar
									alterado={dirty || selecoesAlteradas}
									salvando={isSubmitting}
									bloqueado={Object.keys(errors).length ? "Perfil" : null}
									onDescartar={() => {
										resetForm();
										descartarSelecoes();
									}}
								/>
							)}
						</Form>
					);
				}

				return (
					<Form className="space-y-4">
						{dados}
						{!meuPerfil && podeCargo(cargo)}
						{!meuPerfil && podeFilas(atuacao)}
						<DialogFooter>
							<Button
								type="button"
								variant="outline"
								onClick={handleClose}
								disabled={isSubmitting}
							>
								{i18n.t("userModal.buttons.cancel")}
							</Button>
							<Button type="submit" disabled={isSubmitting}>
								{isSubmitting && <Loader2 className="h-4 w-4 animate-spin" />}
								{meuPerfil
									? "Salvar"
									: userId
									? i18n.t("userModal.buttons.okEdit")
									: i18n.t("userModal.buttons.okAdd")}
							</Button>
						</DialogFooter>
					</Form>
				);
			}}
		</Formik>
	);

	if (pagina) return formulario;

	return (
		<Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
			<DialogContent className="sm:max-w-md">
				{cabecalho}
				{formulario}
			</DialogContent>
		</Dialog>
	);
};

export default UserModal;
